/**
 * App-level notifications for finished jobs and discoveries (OA-220).
 *
 * Two parts, deliberately separate:
 *
 * - A polite live region (`role="status"`) that is always present while the
 *   user is signed in. It carries the text to announce and nothing else.
 *   Failures are announced here too, not through `role="alert"`: a job that
 *   finished in the background is not an urgent, time-sensitive problem with
 *   what the user is doing now (ARIA's definition of an alert), and an
 *   assertive announcement would cut into whatever they are reading or typing
 *   on another page. The text says "failed", so the outcome is not lost.
 *   SC 4.1.3 Status Messages is met either way.
 * - A visible stack of toasts in a named region (`<section aria-label>`, a
 *   landmark screen reader users can jump to). The toasts are not live, so
 *   nothing is read twice, and they never take focus.
 *
 * Toasts stay until dismissed. They link to a page the user may want to open,
 * so a timer would be a time limit on that content (SC 2.2.1), and pausing on
 * hover or focus does not help someone who has not found the toast yet. To
 * keep a busy session from piling them up, only the newest MAX_TOASTS are
 * kept; everything that drops off is still in the job history and the
 * discovery list. Each toast is dismissed with its button or with Escape while
 * focus is in it, and "Dismiss all" clears the stack.
 *
 * Nothing is shown or announced for a job or discovery whose page the user is
 * on: that page already announces its own progress (LiveProgressAnnouncer,
 * OA-56). A toast is also removed when the user reaches its page.
 *
 * Bursts (a bulk upload finishing) are announced as one summary instead of a
 * queue of messages: notifications that arrive within ANNOUNCE_BATCH_MS are
 * collected and announced together.
 *
 * Renders nothing until the realtime client registers as the notification
 * transport (see src/services/notifications.ts), and nothing when signed out.
 */
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import {
  AppNotification,
  isNotificationTransportAvailable,
  onNotificationTransportChange,
  subscribeNotifications,
} from '../../services/notifications';
import HiddenContextText from './hiddenText';
// Side effect: the realtime client registers itself as the notification
// transport (OA-56). Without this it loads only with the job and discovery pages.
import '../../services/realtime';

import './NotificationRegion.css';

/** How many toasts stay on screen; older ones are dropped. */
export const MAX_TOASTS = 5;

/** Notifications arriving within this window are announced as one message. */
export const ANNOUNCE_BATCH_MS = 1000;

// The page of each kind of resource, as routed in App.tsx.
const RESOURCE_BASE = {
  job: '/jobs',
  discovery: '/discover',
} as const;

interface Toast {
  key: string;
  notification: AppNotification;
}

function toastKey(notification: AppNotification): string {
  return `${notification.resource}-${notification.resourceId}`;
}

function resourcePath(notification: AppNotification): string {
  return `${RESOURCE_BASE[notification.resource]}/${encodeURIComponent(notification.resourceId)}`;
}

/**
 * Whether `pathname` is the page of the job or discovery a notification is about.
 *
 * @param {string} pathname - Current location pathname
 * @param {AppNotification} notification - The notification
 * @returns {boolean} True on that job's or discovery's own page
 */
function isOnResourcePage(pathname: string, notification: AppNotification): boolean {
  const path = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return path === resourcePath(notification);
}

/**
 * Add a toast, replacing any older one for the same job or discovery and
 * keeping only the newest MAX_TOASTS.
 *
 * @param {Toast[]} previous - Current toasts
 * @param {Toast} toast - The new toast
 * @returns {Toast[]} The new list
 */
function withToast(previous: Toast[], toast: Toast): Toast[] {
  return [...previous.filter(existing => existing.key !== toast.key), toast].slice(-MAX_TOASTS);
}

function outcomeOf(notification: AppNotification): 'completed' | 'failed' {
  return notification.kind.endsWith('.failed') ? 'failed' : 'completed';
}

/**
 * Move focus somewhere sensible when the focused toast goes away, so it does
 * not fall to <body> (SC 2.4.3): the toast that takes its place, else the one
 * before it, else the main content.
 *
 * @param {Array<HTMLElement | null | undefined>} candidates - Buttons to try, in order
 */
function moveFocusAfterRemoval(candidates: Array<HTMLElement | null | undefined>): void {
  const target = candidates.find(Boolean);
  if (target) {
    target.focus();
    return;
  }
  const main = document.querySelector<HTMLElement>('#main-content');
  if (!main) {
    return;
  }
  if (!main.hasAttribute('tabindex')) {
    // Temporary, as ScrollToTop does for hash targets: gone again on blur.
    main.setAttribute('tabindex', '-1');
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
  }
  main.focus();
}

const NotificationCenter: React.FC = () => {
  const { t } = useTranslation('notifications');
  const { pathname } = useLocation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  // `seq` alternates a trailing no-break space so the same text twice in a row
  // is still a change, and still announced.
  const [announcement, setAnnouncement] = useState({ text: '', seq: 0 });
  const pathnameRef = useRef(pathname);
  const pendingRef = useRef<AppNotification[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissButtons = useRef(new Map<string, HTMLButtonElement>());
  const stackRef = useRef<HTMLElement>(null);

  const messageFor = useCallback(
    (notification: AppNotification) =>
      t(`${notification.resource}.${outcomeOf(notification)}`, {
        label: notification.label ?? t(`unnamed.${notification.resource}`),
      }),
    [t]
  );

  useEffect(() => {
    pathnameRef.current = pathname;
    // The user reached a toast's page (often by its link): it has done its job.
    setToasts(previous => {
      const remaining = previous.filter(toast => !isOnResourcePage(pathname, toast.notification));
      return remaining.length === previous.length ? previous : remaining;
    });
  }, [pathname]);

  useEffect(() => {
    const flush = () => {
      timerRef.current = null;
      const batch = pendingRef.current;
      pendingRef.current = [];
      if (batch.length === 0) {
        return;
      }
      const failed = batch.filter(notification => outcomeOf(notification) === 'failed').length;
      const text =
        batch.length === 1
          ? messageFor(batch[0])
          : t('summary', { count: batch.length, completed: batch.length - failed, failed });
      setAnnouncement(previous => ({ text, seq: previous.seq + 1 }));
    };

    const unsubscribe = subscribeNotifications(notification => {
      if (isOnResourcePage(pathnameRef.current, notification)) {
        return;
      }
      const key = toastKey(notification);
      // A newer notification for the same job (a retry that then completed)
      // replaces the older one rather than sitting beside it.
      setToasts(previous => withToast(previous, { key, notification }));
      pendingRef.current.push(notification);
      timerRef.current ??= setTimeout(flush, ANNOUNCE_BATCH_MS);
    });

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = [];
    };
  }, [messageFor, t]);

  // Reserve the space the fixed stack covers while it is on screen, so it
  // does not hide focused content at the foot of the page (SC 2.4.11). The
  // same approach as ConsentBanner, through a class and a custom property
  // rather than inline styles, so the two never overwrite each other's
  // values.
  const hasToasts = toasts.length > 0;
  useEffect(() => {
    const stack = stackRef.current;
    if (!hasToasts || !stack) {
      return;
    }
    const root = document.documentElement;
    const reserve = () => {
      const height = Math.ceil(stack.getBoundingClientRect().height);
      root.style.setProperty('--notification-stack-reserve', `${height}px`);
    };
    reserve();
    root.classList.add('has-notification-stack');
    const observer = new ResizeObserver(reserve);
    observer.observe(stack);
    return () => {
      observer.disconnect();
      root.classList.remove('has-notification-stack');
      root.style.removeProperty('--notification-stack-reserve');
    };
  }, [hasToasts]);

  const dismiss = useCallback(
    (key: string) => {
      const index = toasts.findIndex(toast => toast.key === key);
      if (index === -1) {
        return;
      }
      const item = dismissButtons.current.get(key)?.closest('li');
      if (item?.contains(document.activeElement)) {
        moveFocusAfterRemoval([
          dismissButtons.current.get(toasts[index + 1]?.key),
          dismissButtons.current.get(toasts[index - 1]?.key),
        ]);
      }
      setToasts(previous => previous.filter(toast => toast.key !== key));
    },
    [toasts]
  );

  const dismissAll = useCallback(() => {
    if (stackRef.current?.contains(document.activeElement)) {
      moveFocusAfterRemoval([]);
    }
    setToasts([]);
  }, []);

  return (
    <>
      <div className="notification-announcer" role="status" aria-live="polite" aria-atomic="true">
        {announcement.text}
        {announcement.seq % 2 === 1 ? ' ' : ''}
      </div>
      {hasToasts && (
        <section ref={stackRef} className="notification-stack" aria-label={t('regionLabel')}>
          <ul className="notification-stack__list">
            {toasts.map(({ key, notification }) => {
              const messageId = `notification-message-${key}`;
              return (
                <li
                  key={key}
                  className={`notification-toast notification-toast--${outcomeOf(notification)}`}
                  onKeyDown={event => {
                    if (event.key === 'Escape' && !event.defaultPrevented) {
                      event.preventDefault();
                      dismiss(key);
                    }
                  }}
                >
                  <p id={messageId} className="notification-toast__message">
                    {messageFor(notification)}
                  </p>
                  <div className="notification-toast__actions">
                    <Link
                      to={resourcePath(notification)}
                      className="notification-toast__link"
                      aria-describedby={messageId}
                    >
                      {/* Same name as the job card's "View Details" link to this page (OA-288):
                          the visible words match, the label is hidden context. */}
                      <HiddenContextText
                        t={t}
                        i18nKey={`${notification.resource}.view`}
                        values={{
                          label: notification.label ?? t(`unnamed.${notification.resource}`),
                        }}
                      />
                    </Link>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-describedby={messageId}
                      ref={element => {
                        if (element) {
                          dismissButtons.current.set(key, element);
                        } else {
                          dismissButtons.current.delete(key);
                        }
                      }}
                      onClick={() => dismiss(key)}
                    >
                      {t('dismiss')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {toasts.length > 1 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm notification-stack__dismiss-all"
              onClick={dismissAll}
            >
              {t('dismissAll')}
            </button>
          )}
        </section>
      )}
    </>
  );
};

/**
 * Mounted once, inside the router, for the whole app.
 *
 * @returns {React.ReactElement | null} The notification region, or nothing when signed out or before the
 *   realtime client is wired in
 */
const NotificationRegion: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const available = useSyncExternalStore(
    onNotificationTransportChange,
    isNotificationTransportAvailable,
    () => false
  );
  if (!isAuthenticated || !available) {
    return null;
  }
  return <NotificationCenter />;
};

export default NotificationRegion;
